import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, debounceTime, from, map, Observable, startWith, Subscription, take, tap } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { EditComponent } from '@workspaces/utils/edit-component';
import { Task } from '@workspaces/models/task';
import { TaskListFetch, taskListFeature } from '@workspaces/store/task-list.feature';
import { KanbanIdPipe } from '@workspaces/utils/pipes/kanban-id.pipe';
import { MatLabel, MatFormField, MatPrefix } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { userManagementFeature, UserManagementFetch } from '@workspaces/store/user-management.feature';
import { WorkspaceUser } from '@workspaces/models/users';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TaskDetailsDialogComponent } from '../task-details/dialog/task-details-dialog.component';
import { CognitoService } from 'src/app/services/cognito.service';
import { IsDueDateExpiredPipe } from '../../utils/pipes/is-due-date-expired.pipe';

@Component({
  selector: 'app-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    KanbanIdPipe,
    MatLabel,
    MatFormField,
    ReactiveFormsModule,
    MatInput,
    MatIcon,
    MatPrefix,
    MatTooltipModule,
    RouterModule,
    IsDueDateExpiredPipe
  ]
})
export class TaskListComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public readonly searchControl = new FormControl('', { nonNullable: true });
  public readonly selectedUsers = new FormControl<string[]>([], { nonNullable: true });
  public tasks: Task[] = [];
  public users$!: Observable<WorkspaceUser[]>;
  public currentUserId = '';
  constructor(
    protected override readonly route: ActivatedRoute,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog,
    private readonly cognitoService: CognitoService
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit() {
    super.ngOnInit();
    const userIdPromise = this.cognitoService.getUserId();
    this.users$ = combineLatest([
      this.store.select(userManagementFeature.selectData).pipe(startWith([] as WorkspaceUser[])),
      from(userIdPromise).pipe(startWith(''), take(1))
    ]).pipe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tap(([_users, currentUserId]) => {
        this.currentUserId = currentUserId;
      }),
      map(([users, currentUserId]) => {
        const currentUserIndex = users.findIndex(user => user.id === currentUserId);

        if (currentUserIndex > -1) {
          const reorderedUsers = [...users];
          const currentUser = reorderedUsers.splice(currentUserIndex, 1)[0];
          reorderedUsers.unshift(currentUser);
          return reorderedUsers;
        }

        return users;
      })
    );

    const allTasks$ = this.store.select(taskListFeature.selectData).pipe(
      map(data => (data.workspaceId === this.workspaceId ? data.tasks : [])),
      startWith([])
    );
    const filterChanges$ = combineLatest({
      searchTerm: this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
      selectedUsers: this.selectedUsers.valueChanges.pipe(startWith(this.selectedUsers.value))
    });

    this.sub.add(
      combineLatest([allTasks$, filterChanges$]).subscribe(([taskList, { searchTerm, selectedUsers }]) => {
        this.tasks = this.filterSearch(searchTerm ?? '', structuredClone(taskList));
        this.tasks = this.filterResponsible(selectedUsers, this.tasks);
      })
    );

    this.setInitialStateFromUrl();
    this.manageUrlUpdates();

    this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
    this.store.dispatch(UserManagementFetch({ workspaceId: this.workspaceId }));
  }

  private getStateFromUrl(queryParams: Params) {
    const initialSearch = queryParams['search'] || '';
    const initialUsersRaw = queryParams['users'];
    const initialUsers = initialUsersRaw
      ? Array.isArray(initialUsersRaw)
        ? initialUsersRaw
        : initialUsersRaw.split(',')
      : [];
    return { initialSearch, initialUsers };
  }

  private setInitialStateFromUrl() {
    const { initialSearch, initialUsers } = this.getStateFromUrl(this.route.snapshot.queryParams);
    this.searchControl.patchValue(initialSearch);
    this.selectedUsers.patchValue(initialUsers);
  }

  private updateQueryParams(params: Record<string, string | null>): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private manageUrlUpdates(): void {
    this.sub.add(
      this.route.queryParams.subscribe(queryParams => {
        const { initialSearch, initialUsers } = this.getStateFromUrl(queryParams);
        this.searchControl.patchValue(initialSearch, { emitEvent: false });
        this.selectedUsers.patchValue(initialUsers, { emitEvent: false });
      })
    );

    this.sub.add(
      this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe(searchTerm => {
        this.updateQueryParams({ search: searchTerm || null });
      })
    );

    this.sub.add(
      this.selectedUsers.valueChanges.subscribe(users => {
        const usersParam = users && users.length > 0 ? users.join(',') : null;
        this.updateQueryParams({ users: usersParam });
      })
    );
  }

  private filterSearch(searchTerm: string, tasks: Task[]): Task[] {
    return tasks.filter(task => {
      const matchesTitle = task.name.toLowerCase().includes(searchTerm);
      const matchesDescription = task.description.toLowerCase().includes(searchTerm);
      const matchesResponsibleName = task.assigned_to?.name.toLowerCase().includes(searchTerm);

      return matchesTitle || matchesDescription || matchesResponsibleName;
    });
  }

  private filterResponsible(selectedUserIds: (string | undefined)[] | null, tasks: Task[]): Task[] {
    if (!selectedUserIds?.length) {
      return tasks;
    }
    return tasks.filter(task => selectedUserIds.includes(task.assigned_to?.id));
  }

  public addNewTask(columnId: string): void {
    const dialogRef = this.dialog.open(TaskDetailsDialogComponent, {
      width: TaskDetailsDialogComponent.width,
      data: { columnId: columnId, workspaceId: this.workspaceId }
    });

    this.sub.add(
      dialogRef.afterClosed().subscribe(() => {
        this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
      })
    );
  }

  public selectUser(selectedUserId: string): void {
    let currentSelectedUserIds = this.selectedUsers.value;
    if (currentSelectedUserIds?.includes(selectedUserId)) {
      currentSelectedUserIds = currentSelectedUserIds.filter(userId => userId !== selectedUserId);
    } else {
      currentSelectedUserIds?.push(selectedUserId);
    }
    this.selectedUsers.patchValue(currentSelectedUserIds);
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
